import React from "react";
import {
  IconBuildingSkyscraper,
  IconArrowLeft,
  IconArrowRight,
} from "@tabler/icons-react";
import SectionCard from "./SectionCard";
import { CompanyDetails, CountryCode } from "../../store/@types/portal";
import {
  BUSINESS_TYPES_BY_COUNTRY,
  COUNTRIES,
} from "../../constants/countryData";
import TextInput from "../elements/form/TextInput";
import SelectInput from "../elements/form/SelectInput";
import TextBox from "../elements/form/TextBox";
import Button from "../elements/form/Button";

type CompanyDetailsStepProps = {
  countryCode: CountryCode;
  details: CompanyDetails;
  onChange: (field: keyof CompanyDetails, value: string) => void;
  onBack: () => void;
  onContinue: () => void;
};

const CompanyDetailsStep: React.FC<CompanyDetailsStepProps> = ({
  countryCode,
  details,
  onChange,
  onBack,
  onContinue,
}) => {
  const country = COUNTRIES.find((c) => c.code === countryCode);
  const businessTypes = BUSINESS_TYPES_BY_COUNTRY[countryCode];

  return (
    <SectionCard
      icon={<IconBuildingSkyscraper size={20} stroke={1.75} />}
      title="Company Information"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <TextInput
            label="Company Name"
            name="companyName"
            type="text"
            value={details.companyName}
            placeholder="Enter desired company name"
            required
            onChange={(e) => onChange("companyName", e.target.value)}
          />
          <SelectInput
            label="Business Type"
            name="businessType"
            status="warning"
            items={businessTypes.map((type) => ({
              id: type.id,
              name: type.name,
            }))}
            value={details.businessType}
            onChange={(e) => onChange("businessType", e.target.value)}
          />
        </div>

        <TextBox
          label="Registered Address"
          name="registeredAddress"
          value={details.registeredAddress}
          placeholder="Full business address"
          status="warning"
          rowsValue={3}
          onChange={(e) => onChange("registeredAddress", e.target.value)}
        />

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <TextInput
            label="Contact Email"
            name="contactEmail"
            type="email"
            value={details.contactEmail}
            placeholder="business@example.com"
            required
            onChange={(e) => onChange("contactEmail", e.target.value)}
          />
          <TextInput
            label="Contact Phone"
            name="contactPhone"
            type="phone"
            value={details.contactPhone}
            placeholder="+234 XXX XXX XXXX"
            required
            onChange={(e) => onChange("contactPhone", e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <TextInput
            label={`Share Capital (${country?.currencyCode ?? ""})`}
            name="shareCapital"
            type="number"
            value={details.shareCapital}
            placeholder="10000"
            required
            onChange={(e) => onChange("shareCapital", e.target.value)}
          />
          <TextInput
            label="Number of Directors"
            name="numberOfDirectors"
            type="number"
            value={details.numberOfDirectors}
            placeholder="2"
            required
            onChange={(e) => onChange("numberOfDirectors", e.target.value)}
          />
        </div>

        <TextBox
          label="Nature of Business"
          name="natureOfBusiness"
          value={details.natureOfBusiness}
          placeholder="Describe your business activities"
          status="warning"
          rowsValue={3}
          onChange={(e) => onChange("natureOfBusiness", e.target.value)}
        />
      </div>

      <div className="mt-8 flex items-center justify-between pt-6">
        <Button
          title="Back"
          type="button"
          onHandler={onBack}
          icon={<IconArrowLeft size={18} />}
          bg_color="bg-white"
          bg_hoverColor="bg-slate-50"
          text_color="text-slate-700"
          className="rounded-full border border-slate-200"
        />
        <Button
          title="Continue to AI Assistant"
          type="button"
          onHandler={onContinue}
          rightIcon={<IconArrowRight size={18} />}
          bg_color="bg-primary-500"
          bg_hoverColor="bg-primary-600"
          className="rounded-full"
        />
      </div>
    </SectionCard>
  );
};

export default CompanyDetailsStep;
